import { useEffect } from 'react';

/** Same env override SP uses in `config/gettingStartedConfig.ts`. */
const DOCS_BASE_URL =
  import.meta.env['VITE_DOCS_BASE_URL'] || 'https://app-docs.directhomeservice.com';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import { useTranslation } from 'react-i18next';
import { useSidebar } from './SidebarContext';
import { useContextStore } from '../../hooks/context/useContextStore';
import { useIsMobile } from '../../hooks/useIsMobile';
import { isImpersonating } from '../../utils/impersonationSession';
import logoMark from '../../assets/images/dhs-mark.png';
import {
  IconLayoutDashboard,
  IconMessage,
  IconBriefcase,
  IconClipboardList,
  IconFileInvoice,
  IconCalendarEvent,
  IconUsers,
  IconChevronLeft,
  IconHelpCircle,
  IconChevronRight,
  IconX,
  IconSearch,
} from '@tabler/icons-react';

interface NavItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  secondaryIcon?: React.ReactNode;
}

const ICON_PROPS = { size: 18, stroke: 1.75 };
const logo = '/logo-02-primary.png';

const navItems: NavItem[] = [
  {
    key: 'dashboard',
    label: 'dashboard',
    icon: <IconLayoutDashboard {...ICON_PROPS} />,
    path: '/dashboard',
  },
  { key: 'inbox', label: 'inbox', icon: <IconMessage {...ICON_PROPS} />, path: '/inbox' },
  {
    key: 'appointments',
    label: 'appointments',
    icon: <IconCalendarEvent {...ICON_PROPS} />,
    path: '/appointments',
  },
  {
    key: 'serviceProviders',
    label: 'serviceProviders',
    icon: <IconUsers {...ICON_PROPS} />,
    path: '/service-providers',
  },
  { key: 'jobs', label: 'jobs', icon: <IconBriefcase {...ICON_PROPS} />, path: '/jobs' },
  {
    key: 'estimates',
    label: 'estimates',
    icon: <IconClipboardList {...ICON_PROPS} />,
    path: '/estimates',
  },
  {
    key: 'invoices',
    label: 'invoices',
    icon: <IconFileInvoice {...ICON_PROPS} />,
    path: '/invoices',
  },
];

const Sidebar = () => {
  const { isCollapsed, toggle, isMobileOpen, closeMobile, openSearch } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { guard } = useUnsavedChanges();
  // Every exit from this rail goes through the guard: a screen holding unapplied
  // edits gets to ask before it is unmounted.
  // Closing the mobile drawer is part of leaving, so it happens inside the guard
  // too. It used to run unconditionally next to it: tapping a nav item with a
  // card mid-edit dismissed the drawer AND raised the prompt, and choosing
  // "Keep editing" left the user on the same screen with the drawer gone.
  const go = (path: string) =>
    guard(() => {
      navigate(path);
      closeMobile();
    });
  const { t } = useTranslation();
  const { clientUser } = useContextStore();
  const isMobile = useIsMobile();

  // Close the drawer on route change and when growing past the mobile breakpoint.
  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  useEffect(() => {
    if (!isMobile) closeMobile();
  }, [isMobile, closeMobile]);

  // Lock background scroll while the drawer is open.
  useEffect(() => {
    if (!isMobile || !isMobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isMobile, isMobileOpen]);

  if (
    !isImpersonating() &&
    !clientUser?.isWelcomeScreenClosed &&
    location.pathname === '/dashboard'
  ) {
    return null;
  }

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  // On mobile the drawer is always shown expanded, regardless of the desktop
  // collapsed preference.
  const showCollapsed = isCollapsed && !isMobile;

  const renderNavItem = (item: NavItem) => {
    const active = isActive(item.path);

    if (showCollapsed) {
      return (
        <button
          key={item.key}
          onClick={() => go(item.path)}
          className={`flex items-center justify-center w-10 h-10 rounded-ds transition-colors ${
            active
              ? 'bg-primary text-white shadow-ds-xs'
              : 'text-ds-text-secondary hover:bg-white/60'
          }`}
          title={t(item.label)}
        >
          {item.icon}
        </button>
      );
    }

    return (
      <button
        key={item.key}
        onClick={() => go(item.path)}
        className={`flex items-center w-full px-3 gap-2 rounded-ds transition-colors ${
          isMobile ? 'h-[52px]' : 'py-3'
        } ${
          active ? 'bg-primary text-white shadow-ds-xs' : 'text-ds-text-secondary hover:bg-white/60'
        }`}
      >
        {item.icon}
        <span className="text-body-md flex-1 text-left truncate">{t(item.label)}</span>
        {item.secondaryIcon && (
          <span className="flex-shrink-0 opacity-60">{item.secondaryIcon}</span>
        )}
      </button>
    );
  };

  return (
    <>
      {/* Backdrop — mobile only */}
      {isMobile && (
        <div
          className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-200 ${
            isMobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}

      <aside
        className={`flex flex-col overflow-visible border-r border-ds-border bg-white transition-all duration-200 ${
          isMobile
            ? `fixed inset-y-0 left-0 z-50 h-dvh w-[278px] max-w-[85vw] shadow-ds-md ${
                isMobileOpen ? 'translate-x-0' : '-translate-x-full'
              }`
            : `relative h-screen flex-shrink-0 ${isCollapsed ? 'w-[64px]' : 'w-[272px]'}`
        }`}
        aria-hidden={isMobile && !isMobileOpen}
      >
        {/* Header */}
        <div className="flex h-[60px] flex-shrink-0 items-center justify-start border-b border-ds-border px-2 py-[5px]">
          <button
            type="button"
            onClick={() => go('/dashboard')}
            className={`flex h-full min-w-0 items-center rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
              showCollapsed ? 'w-full justify-center' : 'w-full justify-start'
            }`}
            aria-label={t('dashboard')}
          >
            <img
              src={showCollapsed ? logoMark : logo}
              alt={showCollapsed ? 'DHS' : 'Direct Home Service'}
              className={
                showCollapsed
                  ? 'h-7 w-7 object-contain'
                  : 'h-full max-w-full object-contain object-left'
              }
            />
          </button>

          {isMobile && (
            <button
              type="button"
              onClick={closeMobile}
              className="ml-auto flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-ds text-ds-text-tertiary transition-colors hover:bg-ds-bg"
              aria-label={t('close', 'Close')}
            >
              <IconX size={20} stroke={1.75} />
            </button>
          )}
        </div>

        {!isMobile && (
          <button
            type="button"
            onClick={toggle}
            className="absolute right-[-11px] top-[46px] z-50 flex h-[22px] w-[22px] items-center justify-center rounded-full border border-ds-border bg-white text-ds-text-tertiary shadow-ds-xs transition-colors hover:bg-ds-bg/50"
            aria-label={
              isCollapsed
                ? t('expandSidebar', 'Expand sidebar')
                : t('collapseSidebar', 'Collapse sidebar')
            }
          >
            {isCollapsed ? (
              <IconChevronRight size={16} stroke={2.25} />
            ) : (
              <IconChevronLeft size={16} stroke={2.25} />
            )}
          </button>
        )}

        {/* Search trigger — opens the global search modal (mirrors the SP web app) */}
        {showCollapsed ? (
          <div className="flex justify-center px-2 py-3">
            <button
              type="button"
              onClick={openSearch}
              className="flex h-9 w-9 items-center justify-center rounded-ds border border-ds-border bg-white text-ds-text-tertiary transition-colors hover:bg-ds-bg/50"
              aria-label={t('search')}
              title={t('search')}
            >
              <IconSearch size={18} stroke={1.75} />
            </button>
          </div>
        ) : (
          <div className="m-3">
            <button
              type="button"
              onClick={openSearch}
              className="flex w-full items-center gap-2 rounded-ds border border-ds-border bg-white px-2.5 py-2 text-ds-text-tertiary transition-colors hover:bg-ds-bg/50"
              aria-label={t('search')}
            >
              <IconSearch size={16} stroke={1.75} className="flex-shrink-0" />
              <span className="flex-1 truncate text-left text-sm font-medium">{t('search')}</span>
              <kbd className="flex-shrink-0 rounded border border-ds-border bg-ds-bg px-1.5 py-0.5 text-[10px] font-semibold text-ds-text-disabled">
                ⌘K
              </kbd>
            </button>
          </div>
        )}

        {/* Nav items — offset 50px from header */}
        <div
          className={`flex flex-1 flex-col items-center gap-1 overflow-y-auto pt-3 pb-3 scrollbar-hide ${
            showCollapsed ? 'px-2' : 'px-3'
          } ${isMobile ? 'pb-[calc(0.75rem_+_env(safe-area-inset-bottom))]' : ''}`}
        >
          <div className="flex flex-col w-full gap-1">{navItems.map(renderNavItem)}</div>

          {/* Help at the bottom, mirroring SP's Sidebar: a rule, then the item.
              SP opens its in-app help panel; the portal has no getting-started
              panel, so this goes straight to the docs. There is no client-portal
              section there yet, so it lands on the root for now. */}
          <div className="mt-auto w-full pt-2">
            <div className="mb-2 h-px w-full bg-[color:var(--border-soft)]" />
            <button
              type="button"
              onClick={() => window.open(DOCS_BASE_URL, '_blank', 'noopener,noreferrer')}
              aria-label={t('helpAndSupport')}
              {...(showCollapsed && { title: t('helpAndSupport') })}
              className={
                showCollapsed
                  ? 'flex items-center justify-center w-10 h-10 mx-auto rounded-ds text-ds-text-secondary transition-colors hover:bg-white/60'
                  : 'flex items-center w-full px-3 py-3 gap-2 rounded-ds text-ds-text-secondary transition-colors hover:bg-white/60'
              }
            >
              <IconHelpCircle size={18} stroke={1.75} className="flex-shrink-0" />
              {!showCollapsed && (
                <span className="text-body-md flex-1 text-left truncate">
                  {t('helpAndSupport')}
                </span>
              )}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
